import React, { useEffect, useState } from 'react';
import { useI18n } from '@/i18n/LanguageContext';
import {
  probeSendingIdentity,
  runLiveInboxTest,
  pollInboxTest,
  verdictTone,
  verdictLabel,
  SEED_HINTS,
  BRAND_DOMAIN,
  PROVIDER_DOMAIN,
  type LiveTestResponse,
} from '@/lib/emailAuth';

/**
 * Admin → Email delivery → "Live inbox test".
 *
 * Sends a REAL transactional message through the production sender to the
 * operator's own Gmail / Outlook seed addresses, then reads the receiving mail
 * server's own SMTP answer back out of the provider event log.
 *
 * Deliberately honest: folder placement is never claimed. A receiver returns
 * the same "250 OK" whether it files the message in the Inbox or in Spam, and
 * it never tells the sender which it chose — so the panel shows the acceptance
 * evidence and the exact header check to run in the seed mailbox.
 */

const Chip: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${
      ok ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-red-50 text-red-700 ring-red-200'
    }`}
  >
    {label} {ok ? 'PASS' : 'FAIL'}
  </span>
);

const AdminInboxTest: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [res, setRes] = useState<LiveTestResponse | null>(null);
  const [gmail, setGmail] = useState('');
  const [outlook, setOutlook] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    const out = await probeSendingIdentity();
    if ('error' in out) setErr(out.error);
    else setRes(out);
  };

  useEffect(() => {
    load();
  }, []);

  const send = async () => {
    const seeds = [gmail, outlook].map((s) => s.trim()).filter(Boolean);
    if (!seeds.length) {
      setErr(es ? 'Agrega al menos una dirección de prueba.' : 'Add at least one seed address.');
      return;
    }
    setBusy(true);
    setErr('');
    const out = await runLiveInboxTest(seeds);
    if ('error' in out) setErr(out.error);
    else setRes(out);
    setBusy(false);
  };

  const repoll = async () => {
    const ids = (res?.results || []).map((r) => r.messageId).filter(Boolean) as string[];
    setBusy(true);
    const out = await pollInboxTest(ids);
    if ('error' in out) setErr(out.error);
    else setRes(out);
    setBusy(false);
  };

  const id = res?.sendingIdentity;
  const results = res?.results || [];

  return (
    <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold tracking-tight text-slate-900">
            {es ? 'Prueba de entrega real' : 'Live inbox test'}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            {es
              ? 'Envía un mensaje real por la ruta de producción y lee la respuesta SMTP del servidor que lo recibe (Gmail, Outlook). Evidencia, no suposiciones.'
              : 'Sends a real message through the production path and reads back the receiving server’s own SMTP response (Gmail, Outlook). Evidence, not assumptions.'}
          </p>
        </div>
        <button
          onClick={load}
          disabled={busy}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          {es ? 'Revisar remitente' : 'Re-check sender'}
        </button>
      </div>

      {err && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700 ring-1 ring-red-200">{err}</p>}

      {/* Which identity is live right now */}
      {id && (
        <div
          className={`mt-5 rounded-xl p-4 ring-1 ${
            id.branded ? 'bg-emerald-50 ring-emerald-200' : 'bg-amber-50 ring-amber-200'
          }`}
        >
          <p className="text-sm font-bold text-slate-900">
            {es ? 'Remitente activo' : 'Active sender'}
          </p>
          <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
            <p className="font-mono text-slate-800">{id.support}</p>
            <p className="font-mono text-slate-800">{id.noreply}</p>
          </div>
          {id.branded && (
            <p className="mt-2 text-xs font-medium text-slate-600">
              {es ? 'Antes' : 'Previously'}:{' '}
              <span className="font-mono line-through">support@{id.previousFromDomain || PROVIDER_DOMAIN}</span> →{' '}
              <span className="font-mono font-bold">support@{BRAND_DOMAIN}</span>
              {id.promotedAt && ` · ${new Date(id.promotedAt).toLocaleString()}`}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip ok={id.alignment.spf} label="SPF" />
            <Chip ok={id.alignment.dkim} label="DKIM" />
            <Chip ok={id.alignment.dmarc} label="DMARC" />
            <span className="inline-flex items-center rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white">
              d={id.signsAs}
            </span>
          </div>
        </div>
      )}

      {/* Seed addresses */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{SEED_HINTS[0].label}</span>
          <input
            type="email"
            value={gmail}
            onChange={(e) => setGmail(e.target.value)}
            placeholder={SEED_HINTS[0].placeholder}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{SEED_HINTS[1].label}</span>
          <input
            type="email"
            value={outlook}
            onChange={(e) => setOutlook(e.target.value)}
            placeholder={SEED_HINTS[1].placeholder}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={send}
          disabled={busy}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? (es ? 'Enviando…' : 'Sending…') : es ? 'Enviar prueba real' : 'Send live test'}
        </button>
        {results.length > 0 && (
          <button
            onClick={repoll}
            disabled={busy}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {es ? 'Actualizar estado' : 'Refresh delivery status'}
          </button>
        )}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-5 space-y-3">
          {results.map((r) => (
            <div key={r.messageId || r.to} className="rounded-xl border border-slate-200 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-slate-900">{r.provider}</span>
                <span className="font-mono text-xs text-slate-500">{r.to}</span>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${verdictTone(r.verdict)}`}>
                  {verdictLabel(r.verdict, es)}
                </span>
              </div>
              <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                <div>
                  <dt className="font-semibold text-slate-500">{es ? 'Remitente' : 'From'}</dt>
                  <dd className="font-mono text-slate-700">{r.from || '—'}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-500">{es ? 'MX receptor' : 'Receiving MX'}</dt>
                  <dd className="break-all font-mono text-slate-700">{r.mxHost || '—'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="font-semibold text-slate-500">
                    {es ? 'Respuesta del servidor receptor' : 'Receiving server’s own response'}
                  </dt>
                  <dd className="break-all font-mono text-slate-700">
                    {r.smtpCode ? `${r.smtpCode} ` : ''}
                    {r.smtpResponse || (es ? 'esperando evento' : 'waiting for event')}
                  </dd>
                </div>
              </dl>
              {r.error && <p className="mt-1 text-xs font-medium text-red-600">{r.error}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Header check + the limitation nobody can engineer away */}
      {res && (
        <div className="mt-5 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <p className="text-sm font-bold text-slate-900">
            {es ? 'Confirmar la carpeta (solo se puede en el buzón)' : 'Confirming the folder (only possible in the mailbox)'}
          </p>
          <p className="mt-1 text-xs text-slate-600">{res.headerCheck.how}</p>
          <ul className="mt-2 space-y-1">
            {res.headerCheck.expect.map((e) => (
              <li key={e} className="font-mono text-[11px] text-slate-700">
                · {e}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs font-semibold text-slate-700">{res.headerCheck.folder}</p>
          <ul className="mt-3 space-y-1 border-t border-slate-200 pt-2">
            {res.limitations.map((l) => (
              <li key={l} className="text-[11px] text-slate-500">
                · {l}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default AdminInboxTest;
